// src/components/theme/ThemeValue.js

import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Group, Rect as KonvaRect } from 'react-konva';
import Checkbox from '@mui/material/Checkbox';
import { NumberRenderer, SPECIAL_CHARACTERS } from '../DigitRenderer';
import { usageOptions } from '../ThemeLists';
import CustomColorPicker from '../CustomColorPicker';
import styles from './styles.module.css';
import { clamp, snapToGridFn, boundDrag } from '../CanvasTools';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../ScreenProps';

// ThemeValueSettings Component
export const ThemeValueSettings = ({ element, updateElement }) => {
  // Memoize the sorted usage options to prevent unnecessary sorting on each render
  const sortedUsageOptions = React.useMemo(() => {
    return [...usageOptions].sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  // Handler for digitsBefore change
  const handleDigitsBeforeChange = (e) => {
    const digitsBefore = parseInt(e.target.value, 10);
    if (!isNaN(digitsBefore) && digitsBefore >= 0) {
      updateElement(element.id, 'digitsBefore', digitsBefore);
    }
  };

  // Handler for digitsAfter change
  const handleDigitsAfterChange = (e) => {
    const digitsAfter = parseInt(e.target.value, 10);
    if (!isNaN(digitsAfter) && digitsAfter >= 0) {
      updateElement(element.id, 'digitsAfter', digitsAfter);
    }
  };

  // Handler for brake digitsBefore change
  const handleBrakeDigitsBeforeChange = (e) => {
    const brakeDigitsBefore = parseInt(e.target.value, 10);
    if (!isNaN(brakeDigitsBefore) && brakeDigitsBefore >= 0) {
      updateElement(element.id, 'brakeDigitsBefore', brakeDigitsBefore);
    }
  };

  // Handler for brake digitsAfter change
  const handleBrakeDigitsAfterChange = (e) => {
    const brakeDigitsAfter = parseInt(e.target.value, 10);
    if (!isNaN(brakeDigitsAfter) && brakeDigitsAfter >= 0) {
      updateElement(element.id, 'brakeDigitsAfter', brakeDigitsAfter);
    }
  };

  // Handler for size change
  const handleSizeChange = (e) => {
    const size = parseInt(e.target.value, 10);
    if (!isNaN(size) && size > 0) {
      updateElement(element.id, 'size', size);
    }
  };

  // Handler for usage select change
  const handleUsageChange = (e) => {
    updateElement(element.id, 'usage', e.target.value);
  };

  // Handler for brake usage select change
  const handleBrakeUsageChange = (e) => {
    updateElement(element.id, 'brakeUsage', e.target.value);
  };

  // Handlers for X and Y position changes
  const handleXChange = (e) => {
    const newX = parseInt(e.target.value, 10);
    if (!isNaN(newX)) {
      updateElement(element.id, 'x', newX);
    }
  };

  const handleYChange = (e) => {
    const newY = parseInt(e.target.value, 10);
    if (!isNaN(newY)) {
      updateElement(element.id, 'y', newY);
    }
  };

  // Handler for brake enable/disable
  const handleBrakeEnableChange = (e) => {
    const isBrakeEnabled = e.target.checked;
    updateElement(element.id, 'brakeEnabled', isBrakeEnabled);
    if (!isBrakeEnabled) {
      // Reset brake-related fields if brake is disabled
      updateElement(element.id, 'brakeUsage', '0');
      updateElement(element.id, 'brakeDigitsBefore', 2);
      updateElement(element.id, 'brakeDigitsAfter', 0);
      updateElement(element.id, 'brakeTestValue', '');
    }
  };

  // Handler for normal test value change
  const handleTestValueChange = (e) => {
    const input = e.target.value;
    const maxDigits = element.digitsBefore + element.digitsAfter + (element.digitsAfter > 0 ? 1 : 0); // +1 for decimal point if applicable
    if (input.length <= maxDigits) {
      updateElement(element.id, 'testValue', input);
    } else if (maxDigits) {
      updateElement(element.id, 'testValue', input.slice(0, maxDigits));
    }
  };

  // Handler for brake test value change
  const handleBrakeTestValueChange = (e) => {
    const input = e.target.value;
    const maxDigits = element.brakeDigitsBefore + element.brakeDigitsAfter + (element.brakeDigitsAfter > 0 ? 1 : 0); // +1 for decimal point if applicable
    if (input.length <= maxDigits) {
      updateElement(element.id, 'brakeTestValue', input);
    } else if (maxDigits) {
      updateElement(element.id, 'brakeTestValue', input.slice(0, maxDigits));
    }
  };

  return (
    <div>
      {/* X Position */}
      <label>
        X Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_WIDTH - 1}
          value={element.x}
          onChange={handleXChange}
          style={{ marginLeft: '10px', width: '60px' }}
        />
      </label>

      {/* Y Position */}
      <label>
        Y Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_HEIGHT - 1}
          value={element.y}
          onChange={handleYChange}
          style={{ marginLeft: '10px', width: '60px' }}
        />
      </label>

      {/* Font Size */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        Font Size:
        <input
          className={styles.inputNumber}
          type="number"
          min="3"
          value={element.size}
          onChange={handleSizeChange}
          style={{ marginLeft: '10px', width: '60px' }}
        />
      </label>

      {/* Color */}
      <CustomColorPicker
        label="Color"
        color={element.color}
        onChange={(value) => {
          updateElement(element.id, 'color', value);
        }}
      />

      {/* Usage */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        Usage:
        <br />
        <select
          className={styles.select200}
          value={element.usage || '0'}
          onChange={handleUsageChange}
          style={{ marginLeft: '10px', width: '90%' }}
        >
          {sortedUsageOptions.map((option) => (
            <option key={`usage-${option.value}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      {/* Digits Before */}
      <label>
        Number of digits before comma (including the '-' sign):
      </label>
      <input
        className={styles.inputNumber}
        type="number"
        min="1"
        max="6"
        value={element.digitsBefore}
        onChange={handleDigitsBeforeChange}
        style={{ marginLeft: '10px', width: '60px' }}
      />

      {/* Digits After */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        Number of digits after comma:
      </label>
      <input
        className={styles.inputNumber}
        type="number"
        min="0"
        max="2"
        value={element.digitsAfter}
        onChange={handleDigitsAfterChange}
        style={{ marginLeft: '10px', width: '60px' }}
      />

      {/* Normal Test Value */}
      <label>
        Test Value:
        <input
          className={styles.inputText}
          type="text"
          value={element.testValue || ''}
          onChange={handleTestValueChange}
          style={{ marginLeft: '10px', width: '100px' }}
          placeholder="Enter test value"
        />
      </label>

      {/* Brake Enable */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        Enable Brake Usage:
        <Checkbox
          checked={element.brakeEnabled || false}
          onChange={handleBrakeEnableChange}
          style={{ marginLeft: '10px' }}
        />
      </label>
      {element.brakeEnabled && (
        <div style={{ marginTop: '10px', paddingLeft: '20px', borderLeft: '10px solid #9013FE' }}>
          {/* Brake Usage Selection */}
          <label>
            Brake Usage:
            <br />
            <select
              className={styles.select200}
              value={element.brakeUsage || '0'}
              onChange={handleBrakeUsageChange}
              style={{ marginLeft: '10px', width: '90%' }}
            >
              {sortedUsageOptions.map((option) => (
                <option key={`brake-usage-${option.value}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {/* Brake Digits Before */}
          <label style={{ display: 'block', marginTop: '10px' }}>
            Number of digits before comma (including the '-' sign):
            <br />
            <input
              className={styles.inputNumber}
              type="number"
              min="1"
              max="6"
              value={element.brakeDigitsBefore}
              onChange={handleBrakeDigitsBeforeChange}
              style={{ marginLeft: '10px', width: '60px' }}
            />
          </label>

          {/* Brake Digits After */}
          <label style={{ display: 'block', marginTop: '10px' }}>
            Number of digits after comma:
            <br />
            <input
              className={styles.inputNumber}
              type="number"
              min="0"
              max="2"
              value={element.brakeDigitsAfter}
              onChange={handleBrakeDigitsAfterChange}
              style={{ marginLeft: '10px', width: '60px' }}
            />
          </label>

          {/* Brake Test Value */}
          <label style={{ display: 'block', marginTop: '10px' }}>
            Test Value:
            <input
              className={styles.inputText}
              type="text"
              value={element.brakeTestValue || ''}
              onChange={handleBrakeTestValueChange}
              style={{ marginLeft: '10px', width: '100px' }}
              placeholder="Enter brake test value"
            />
          </label>
        </div>
      )}
    </div>
  );
};

ThemeValueSettings.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    digitsBefore: PropTypes.number.isRequired,
    digitsAfter: PropTypes.number.isRequired,
    brakeEnabled: PropTypes.bool.isRequired,
    brakeUsage: PropTypes.string.isRequired,
    brakeDigitsBefore: PropTypes.number.isRequired,
    brakeDigitsAfter: PropTypes.number.isRequired,
    brakeTestValue: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
    usage: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    testValue: PropTypes.string.isRequired,
  }).isRequired,
  updateElement: PropTypes.func.isRequired,
};

const ThemeValue = ({
  element,
  selectedElement,
  setSelectedElement,
  updateElement,
  isBrakeActive,
  render,editAllowed,
  scale,
  snapToGridEnabled,
  gridSize
}) => {
  const groupRef = useRef();

  // Spacing between characters
  const spacing = element.size / 2;

  const handleDragEnd = (e) => {
    const { x, y } = e.target.position();
    // Update the element's x to account for right alignment
    updateElement(element.id, 'x', parseInt(x + getTotalWidth(), 10));
    updateElement(element.id, 'y', parseInt(y, 10));
  };

  // Determine which value to display (normal or brake)
  const isBrakeMode = isBrakeActive && element.brakeEnabled;
  const currentDigitsBefore = isBrakeMode
    ? element.brakeDigitsBefore ?? 2
    : element.digitsBefore ?? 2;
  const currentDigitsAfter = isBrakeMode
    ? element.brakeDigitsAfter ?? 0
    : element.digitsAfter ?? 0;
  const testValue = isBrakeMode
    ? element.brakeTestValue ?? ''
    : element.testValue ?? '';

  // Calculate maximum allowed digits
  const maxDigits = currentDigitsBefore + (currentDigitsAfter > 0 ? 1 : 0) + currentDigitsAfter;

  // Trim or pad the test value to match the expected length
  let displayValue = testValue.slice(-maxDigits); // Keep the rightmost characters

  while (displayValue.length < maxDigits) {
    displayValue = ' ' + displayValue; // Pad with spaces on the left if necessary
  }

  // Function to calculate the total width of the rendered number
  const getTotalWidth = () => {
    let totalWidth = 0;
    const chars = displayValue.split('');
    chars.forEach((ch, index) => {
      let width = 0;
      if (ch >= '0' && ch <= '9') {
        width = element.size * 3;
      } else if (SPECIAL_CHARACTERS[ch]) {
        if (ch === '.' || ch === ':') {
          width = element.size;
        } else if (ch === '-') {
          width = element.size * 2;
        } else if (ch === ' ') {
          width = element.size * 3;
        } else if (ch === '/') {
          width = element.size * 3;
        } else {
          width = element.size * 3; // Default width
        }
      }

      totalWidth += width;

      // Add spacing if not the last character
      if (index < chars.length - 1) {
        totalWidth += spacing;
      }
    });
    return totalWidth;
  };

  const totalWidth = getTotalWidth();

  // Determine if this element is selected
  const isSelected = selectedElement && selectedElement.id === element.id;

  return (
    <Group
      x={element.x - totalWidth}
      y={element.y}
      draggable={!render && editAllowed}
      dragBoundFunc={(pos) => { return boundDrag(pos, scale, snapToGridEnabled, gridSize, SCREEN_WIDTH, SCREEN_HEIGHT, element.size, element.size) }}
      onDragEnd={handleDragEnd}
      onClick={() => setSelectedElement(element)}
      ref={groupRef}
    >
      <NumberRenderer
        number={displayValue}
        x={totalWidth}
        y={0}
        size={element.size}
        color={element.color}
        spacing={spacing}
      />
      {!render && (
        <KonvaRect
          x={0}
          y={0}
          width={totalWidth}
          height={element.size * 5}
          stroke="#1976d2"
          strokeWidth={0.5}
          dash={isSelected ? [1, 1] : [0, 0]}
        />
      )}
    </Group>
  );
};

ThemeValue.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    digitsBefore: PropTypes.number.isRequired,
    digitsAfter: PropTypes.number.isRequired,
    brakeEnabled: PropTypes.bool.isRequired,
    brakeUsage: PropTypes.string.isRequired,
    brakeDigitsBefore: PropTypes.number.isRequired,
    brakeDigitsAfter: PropTypes.number.isRequired,
    brakeTestValue: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
    usage: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    testValue: PropTypes.string.isRequired,
  }).isRequired,
  selectedElement: PropTypes.object,
  setSelectedElement: PropTypes.func.isRequired,
  updateElement: PropTypes.func.isRequired,
  isBrakeActive: PropTypes.bool.isRequired,
  render: PropTypes.bool.isRequired,
  editAllowed: PropTypes.bool,
};

export default ThemeValue;
